import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as api from '../../api/Purchase/transferRecieptOthersVerificationAPI';

const err = (e, fallback) => e?.Message || e?.message || fallback;

export const fetchTransferRecieptOthersQueue = createAsyncThunk(
    'transferRecieptOthersVerification/fetchQueue',
    async ({ roleId, uid }, { rejectWithValue }) => {
        try { return await api.getTransferRecieptOthersQueue(roleId, uid); }
        catch (e) { return rejectWithValue(err(e, 'Failed to fetch MRR verification queue')); }
    }
);

export const fetchTransferRecieptOtherItems = createAsyncThunk(
    'transferRecieptOthersVerification/fetchItems',
    async ({ mrrno, type }, { rejectWithValue }) => {
        try { return await api.getTransferRecieptOtherItems(mrrno, type); }
        catch (e) { return rejectWithValue(err(e, 'Failed to fetch MRR item details')); }
    }
);

export const verifyTransferRecieptOther = createAsyncThunk(
    'transferRecieptOthersVerification/verifyOtherStore',
    async (payload, { rejectWithValue }) => {
        try { return await api.verifyTransferRecieptOther(payload); }
        catch (e) { return rejectWithValue(err(e, 'Failed to submit verification')); }
    }
);

export const verifyItemTransferVendorOthers = createAsyncThunk(
    'transferRecieptOthersVerification/verifyVendor',
    async (payload, { rejectWithValue }) => {
        try { return await api.verifyItemTransferVendorOthers(payload); }
        catch (e) { return rejectWithValue(err(e, 'Failed to submit verification')); }
    }
);

const initialState = {
    queue: [], queueLoading: false, queueError: null,
    items: [], itemsLoading: false, itemsError: null,
    verifyLoading: false, verifyError: null, verifyResult: null,
};

const transferRecieptOthersVerificationSlice = createSlice({
    name: 'transferRecieptOthersVerification',
    initialState,
    reducers: {
        clearTransferRecieptOtherItems: (s) => { s.items = []; s.itemsError = null; },
        clearVerifyResult: (s) => { s.verifyResult = null; s.verifyError = null; },
        resetTransferRecieptOthersVerification: () => initialState,
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchTransferRecieptOthersQueue.pending, (s) => { s.queueLoading = true; s.queueError = null; })
            .addCase(fetchTransferRecieptOthersQueue.fulfilled, (s, a) => { s.queueLoading = false; s.queue = a.payload?.Data || []; })
            .addCase(fetchTransferRecieptOthersQueue.rejected, (s, a) => { s.queueLoading = false; s.queueError = a.payload; s.queue = []; });

        builder
            .addCase(fetchTransferRecieptOtherItems.pending, (s) => { s.itemsLoading = true; s.itemsError = null; })
            .addCase(fetchTransferRecieptOtherItems.fulfilled, (s, a) => { s.itemsLoading = false; s.items = a.payload?.Data || []; })
            .addCase(fetchTransferRecieptOtherItems.rejected, (s, a) => { s.itemsLoading = false; s.itemsError = a.payload; s.items = []; });

        [verifyTransferRecieptOther, verifyItemTransferVendorOthers].forEach((thunk) => {
            builder
                .addCase(thunk.pending, (s) => { s.verifyLoading = true; s.verifyError = null; })
                .addCase(thunk.fulfilled, (s, a) => { s.verifyLoading = false; s.verifyResult = a.payload?.Data ?? a.payload; })
                .addCase(thunk.rejected, (s, a) => { s.verifyLoading = false; s.verifyError = a.payload; });
        });
    },
});

export const {
    clearTransferRecieptOtherItems, clearVerifyResult, resetTransferRecieptOthersVerification,
} = transferRecieptOthersVerificationSlice.actions;

export const selectTROQueue = (s) => s.transferRecieptOthersVerification.queue;
export const selectTROQueueLoading = (s) => s.transferRecieptOthersVerification.queueLoading;
export const selectTROQueueError = (s) => s.transferRecieptOthersVerification.queueError;
export const selectTROItems = (s) => s.transferRecieptOthersVerification.items;
export const selectTROItemsLoading = (s) => s.transferRecieptOthersVerification.itemsLoading;
export const selectTROItemsError = (s) => s.transferRecieptOthersVerification.itemsError;
export const selectTROVerifyLoading = (s) => s.transferRecieptOthersVerification.verifyLoading;
export const selectTROVerifyError = (s) => s.transferRecieptOthersVerification.verifyError;
export const selectTROVerifyResult = (s) => s.transferRecieptOthersVerification.verifyResult;

export default transferRecieptOthersVerificationSlice.reducer;
